import type { CoachMode, MemoryCategory, ProfileType, ResponseStyle } from "@shared/constants";
import { t } from "@shared/i18n";

export type Profile = {
  userId: string;
  profileType: ProfileType | null;
  sector: string | null;
  companyName: string | null;
  goals: string[];
  challenges: string[];
  priorityFeatures: string[];
  workHours: { days: number[]; start: string; end: string; focusPeriods: ("morning" | "afternoon" | "evening")[] } | null;
  timezone: string;
  locale: string;
  currency: string;
  theme: "system" | "light" | "dark";
  density: "comfortable" | "compact";
  adaptiveProfileEnabled: boolean;
  memoryEnabled: boolean;
  coachDataAccess: boolean;
  coachActionsEnabled: boolean;
  aiProvider: string | null;
  aiModel: string | null;
  aiResponseStyle: ResponseStyle;
  onboardingStep: number;
  aiConsentAt: string | null;
  marketingOptInAt: string | null;
  onboardingCompletedAt: string | null;
};

export type PlanId = "free" | "starter" | "pro" | "business";
export type FeatureId = "branding" | "integrations" | "marketing_unlimited" | "growth_ideas" | "email_reminders" | "voice_mode" | "transcription" | "api";
export const PLAN_NAMES: Record<PlanId, string> = { free: t("Découverte"), starter: t("Essentiel"), pro: t("Pro"), business: t("Business") };

export type Me = {
  user: { id: string; name: string; email: string; emailVerified: boolean; image: string | null; createdAt: string };
  profile: Profile;
  workspace: { id: string; name: string; plan: PlanId; isPersonal: boolean; role: "owner" | "admin" | "manager" | "member" | "guest" };
  capabilities: { google: boolean; emailVerificationRequired: boolean; transcription: boolean };
  usage: { used: number; limit: number; plan: PlanId };
  features: Record<FeatureId, boolean>;
  flags: Record<"automations" | "team" | "integrations" | "meetings" | "voice", boolean>;
  isAdmin: boolean;
};

export type ConversationSummary = {
  id: string;
  title: string;
  mode: CoachMode;
  archivedAt: string | null;
  lastMessageAt: string;
  createdAt: string;
};

export type Message = {
  id: string;
  conversationId: string;
  role: "user" | "assistant";
  content: string;
  status: "complete" | "streaming" | "error" | "stopped";
  meta?: { sources?: { type: string; count: number; label: string }[] } | null;
  provider: string | null;
  model: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  errorCode: string | null;
  createdAt: string;
};

export type Conversation = ConversationSummary & { messages: Message[]; proposals: Proposal[] };

export type Memory = {
  id: string;
  category: MemoryCategory;
  content: string;
  enabled: boolean;
  source: string;
  createdAt: string;
  updatedAt: string;
};

export type ProviderStatus = {
  id: string;
  label: string;
  configured: boolean;
  isDefault: boolean;
  defaultModel: string | null;
  models: { id: string; label?: string }[];
  status: "ok" | "error" | "not_configured";
  error?: string;
};

export type TaskStatus = "todo" | "in_progress" | "blocked" | "done" | "cancelled";

export type Task = {
  id: string;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: number;
  dueAt: string | null;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  estimatedMinutes: number | null;
  actualMinutes: number | null;
  category: string | null;
  tags: string[];
  energy: "high" | "medium" | "low" | null;
  projectId: string | null;
  projectName?: string | null;
  projectColor?: string | null;
  assigneeId: string | null;
  source: string;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  dependsOn: string[];
};

export type TaskDetail = Task & {
  dependencies: { id: string; title: string; status: TaskStatus }[];
  blocking: { id: string; title: string; status: TaskStatus }[];
  activity: { id: string; action: string; changes: Record<string, { from: unknown; to: unknown }> | null; createdAt: string; userName: string | null }[];
};

export type Project = {
  id: string;
  name: string;
  description: string | null;
  status: "planned" | "active" | "paused" | "done" | "archived";
  color: string;
  clientId: string | null;
  clientName?: string | null;
  startDate: string | null;
  dueDate: string | null;
  mission: { objective: string; successMetrics: string[]; risks: string[]; assumptions: string[] } | null;
  total: number;
  done: number;
  overdue: number;
  blocked: number;
  remainingMinutes: number;
};

export type CalendarOccurrence = {
  id: string;
  title: string;
  description: string | null;
  location: string | null;
  startAt: string;
  endAt: string;
  allDay: boolean;
  kind: "event" | "focus" | "task_block" | "break";
  category: string | null;
  color: string | null;
  recurrence: "none" | "daily" | "weekdays" | "weekly" | "monthly";
  recurrenceUntil: string | null;
  reminderMinutes: number | null;
  occurrenceStart: string;
  occurrenceEnd: string;
  isRecurring: boolean;
};

export type CalendarTask = Pick<Task, "id" | "title" | "status" | "priority" | "dueAt" | "scheduledStart" | "scheduledEnd" | "projectId">;

export type PlanBlock = { kind: "task" | "break"; taskId?: string; title: string; start: string; end: string; reason: string; estimated: boolean };
export type DayPlan = {
  day: string;
  workday: boolean;
  window?: { start: string; end: string };
  busy: { title: string; start: string; end: string; allDay: boolean }[];
  fixed?: { taskId: string; title: string; start: string; end: string }[];
  blocks: PlanBlock[];
  unscheduled: { taskId: string; title: string; reason: string }[];
  stats: { availableMinutes: number; plannedMinutes: number; demandMinutes: number; overload: boolean; loadRatio: number };
  assumptions: string[];
};

export type Proposal = {
  id: string;
  tool: string;
  summary: string;
  payload: Record<string, unknown>;
  status: "pending" | "applied" | "dismissed" | "failed";
  result: { type: string; id: string; label: string; href?: string } | null;
  error: string | null;
  sourceId: string;
};

export type BriefingItem = { kind: "risk" | "opportunity" | "info"; severity: "high" | "medium" | "low"; title: string; detail: string; href?: string; basis: string };
export type Briefing = {
  facts: {
    date: string;
    workday: boolean;
    dueToday: { id: string; title: string; priority: number; dueAt: string }[];
    overdue: { id: string; title: string; priority: number; dueAt: string }[];
    scheduled: { id: string; title: string; start: string; end: string }[];
    events: { id: string; title: string; start: string; end: string; allDay: boolean; kind: string }[];
    freeSlots: { start: string; end: string; minutes: number }[];
    priorities: { taskId: string; title: string; why: string }[];
    risks: BriefingItem[];
    opportunities: BriefingItem[];
  };
  narrative: { headline: string; priorities: { title: string; why: string; taskId?: string | null }[]; advice: string; watchouts: string[]; suggestion: string } | null;
  narrativeModel: string | null;
};

export type MissionPlan = {
  title: string;
  summary: string;
  questions: string[];
  assumptions: string[];
  milestones: { title: string; dueOffsetDays?: number | null }[];
  tasks: { ref: string; title: string; description?: string | null; estimatedMinutes?: number | null; priority: number; dueOffsetDays?: number | null; dependsOn: string[]; milestone?: string | null }[];
  risks: string[];
  successMetrics: string[];
  documents: string[];
};

export type Transaction = {
  id: string;
  date: string;
  label: string;
  amountCents: number;
  type: "income" | "expense";
  category: string;
  status: "planned" | "pending" | "paid" | "cancelled";
  paymentMethod: string | null;
  clientId: string | null;
  clientName?: string | null;
  projectId: string | null;
  documentId: string | null;
  note: string | null;
  recurrence: "none" | "monthly" | "quarterly" | "yearly";
  recurrenceUntil: string | null;
  importBatch: string | null;
};

export type Client = { id: string; name: string; company: string | null; email: string | null; phone: string | null; address: string | null; siret: string | null; vatNumber: string | null; notes: string | null; archivedAt: string | null };

export type FinanceSummary = {
  from: string;
  to: string;
  incomeCents: number;
  expenseCents: number;
  netCents: number;
  count: number;
  previous: { from: string; to: string; incomeCents: number; expenseCents: number };
  balance: { balanceCents: number; openingBalanceCents: number; openingBalanceDate: string | null };
  byCategory: { type: "income" | "expense"; category: string; total: number }[];
  monthly: { month: string; incomeCents: number; expenseCents: number }[];
  recurring: Transaction[];
  upcoming: Transaction[];
};

export type ForecastScenario = { points: { date: string; balanceCents: number }[]; lowest: { date: string; balanceCents: number }; negativeFrom: string | null; endBalanceCents: number; inflowsCents: number; outflowsCents: number };
export type Forecast = { horizonDays: number; today: string; scenarios: Record<"prudent" | "central" | "optimistic", ForecastScenario>; assumptions: string[]; disclaimer: string };

export type Hypothesis =
  | { kind: "price_increase"; percent: number; from: string }
  | { kind: "expense_cut"; percent: number; from: string }
  | { kind: "hire"; monthlyCostCents: number; from: string }
  | { kind: "purchase"; amountCents: number; date: string; label?: string }
  | { kind: "new_client"; monthlyCents: number; from: string; label?: string }
  | { kind: "payment_delay"; days: number };

export type BusinessSettings = {
  companyName: string | null; legalForm: string | null; address: string | null; siret: string | null; vatNumber: string | null; vatApplicable: boolean; defaultVatRate: number;
  email: string | null; phone: string | null; iban: string | null; bic: string | null; invoicePrefix: string; quotePrefix: string; numberPadding: number; paymentTermsDays: number;
  latePenaltyText: string; currency: string; openingBalanceCents: number; openingBalanceDate: string | null;
  website: string | null; rcs: string | null; shareCapital: string | null; brandPrimary: string; brandSecondary: string; docStyle: "modern" | "classic" | "minimal"; docFooterNote: string | null; hasLogo: boolean; logoUpdatedAt: string | null;
};

export type DocLine = { description: string; quantity: number; unitPriceCents: number; vatRate: number };
export type DocumentRow = {
  id: string;
  type: string;
  title: string;
  status: "draft" | "final" | "sent" | "accepted" | "rejected" | "paid" | "cancelled";
  number: string | null;
  clientId: string | null;
  clientName?: string | null;
  projectId: string | null;
  issueDate: string | null;
  dueDate: string | null;
  currency: string;
  content: string;
  excerpt?: string;
  data: { lines?: DocLine[]; notes?: string; paymentTerms?: string; validityDays?: number; discountCents?: number };
  subtotalCents: number;
  vatCents: number;
  totalCents: number;
  paidAt: string | null;
  version: number;
  aiGenerated: boolean;
  archivedAt: string | null;
  overdue?: boolean;
  updatedAt: string;
  createdAt: string;
};
export type DocumentDetail = DocumentRow & { versions: { id: string; version: number; note: string | null; createdAt: string }[]; client: Client | null };
